import { QueryResult } from '@apollo/client'
import { navigate, RouteComponentProps } from '@reach/router'
import * as React from 'react'
import styled from 'styled-components'
import { InlineErrorBoundary } from '../../../../common/src/components/InlineErrorBoundary'
import { handleError } from '../../../../common/src/data/errors'
import { TestIds } from '../../../../common/src/data/testids'
import keyboard, { KeyAction } from '../../../../common/src/keyboard'
import {
  FetchUserContextQuery,
  FetchInboxQuery,
  FetchInboxQueryVariables,
  FetchTasksPageQuery,
  FetchTasksPageQueryVariables,
} from '../../../../common/src/types.gen'
import { fetchInboxQuery, fetchTasksPageQuery } from '../../graphql/queries/InboxQuery'
import { maybeNotifyUpdate } from '../App'
import { PollingQuery } from '../components/PollingQuery'
import { AppRouteParams, getTaskPath, getThreadPath, routeParams } from '../route'
import TaskDetail from '../tasks/TaskDetail'
import InboxSidebar, { TasksSidebar } from './InboxSidebar'
import ThreadDetail from './ThreadDetail'

const NAV_THREAD_FMT = 'Nav thread %d'

interface InboxPageProps extends RouteComponentProps, AppRouteParams {
  user: FetchUserContextQuery['self']
}

export default class InboxPage extends React.PureComponent<InboxPageProps> {
  render() {
    const { threadId, taskId, contactId } = this.props

    const variables: FetchInboxQueryVariables = {
      ...routeParams(this.props),
      hasThreadId: Boolean(threadId),
      hasTaskIdAndContactId: Boolean(taskId) && Boolean(contactId),
    }

    return (
      <PollingQuery query={fetchInboxQuery} variables={variables}>
        {(res: QueryResult<FetchInboxQuery, FetchInboxQueryVariables>) => <InboxPageImpl {...this.props} {...res} />}
      </PollingQuery>
    )
  }
}

type InboxPageImplProps = InboxPageProps & QueryResult<FetchInboxQuery, FetchInboxQueryVariables>

interface InboxPageImplState {
  selectedThread?: FetchInboxQuery['thread']
  threads?: FetchInboxQuery['self']['unarchivedThreads']
}

class InboxPageImpl extends React.PureComponent<InboxPageImplProps, InboxPageImplState> {
  constructor(props: InboxPageImplProps) {
    super(props)
    this.state = {
      selectedThread: undefined,
      threads: undefined,
    }
  }

  componentDidMount() {
    keyboard.addListener(this.onKeyboardBound)
  }

  componentWillUnmount() {
    keyboard.removeListener(this.onKeyboardBound)
  }

  static getDerivedStateFromProps(props: InboxPageImplProps): InboxPageImplState {
    const { data } = props

    if (data == null) {
      return {}
    }

    const selectedThread = data.thread

    // The unarchivedThreads GraphQL field always returns unarchived threads
    // However! When we archive a thread, that UpdateThreadMutation will update the Apollo cache.
    // This causes App to rerender with unarchivedThreads now containing an archived thread.
    const threads = data.self.unarchivedThreads.filter(t => !t.isArchived)

    if (selectedThread && !selectedThread.isArchived && !threads.some(t => t.id === selectedThread.id)) {
      // Quick unarchive
      threads.push(selectedThread)
      maybeNotifyUpdate(selectedThread)
    }
    threads.sort((a, b) => b.sortTime.localeCompare(a.sortTime))

    const { taskId, threadId, contactId } = props

    if (threadId != null) {
      // We are on a page for a specific thread
      if (selectedThread) {
        return { selectedThread, threads }
      }
    } else if (taskId != null && contactId !== null) {
      if (data.contactThread) {
        // Redirect from /inbox/threads/<task-id>/<contact-id> to /inbox/threads/<thread-id>
        navigate(getThreadPath(data.contactThread.id), { replace: true }).catch(handleError)
      }
    } else if (threads.length > 0) {
      // Show the first thread
      // Redirect from /inbox to eg /inbox/thread/123
      navigate(getThreadPath(threads[0].id), { replace: true }).catch(handleError)
    }

    return { selectedThread, threads }
  }

  render() {
    const { data, user } = this.props

    console.log(
      'InboxPage render: threadId %s thread %s',
      data?.thread?.id,
      data?.thread?.contactThem.firstName + ' ' + data?.thread?.contactThem.lastName
    )

    const { selectedThread, threads } = this.state

    return (
      <InboxWrap>
        <InlineErrorBoundary componentName="InboxSidebar">
          <InboxSidebar threads={threads} selectedThreadId={selectedThread?.id} />
        </InlineErrorBoundary>
        <InboxContent data-testid={TestIds.inboxContent}>
          {selectedThread && (
            <InlineErrorBoundary componentName="ThreadDetail">
              <ThreadDetail
                user={user}
                thread={selectedThread}
                onArchiveThread={this.onArchiveBound}
                maybeClearSelection={this.maybeClearSelection}
              />
            </InlineErrorBoundary>
          )}
        </InboxContent>
      </InboxWrap>
    )
  }

  private onArchiveBound = () => {
    const { threadId } = this.props

    const { threads } = this.state

    if (threads == null || threads.length === 0) {
      return
    }

    // The thread we were on was just archived. Choose a different thread
    const isLastThread = threads[threads.length - 1].id === Number(threadId)

    const offset = isLastThread ? -1 : 1
    this.onNav(offset) // Archive => change to different thread

    // TODO: stop double fetching every time the thread id changes
  }

  private maybeClearSelection = () => {
    const { threads } = this.state
    if (threads == null) {
      return
    }

    if (threads.length === 0) {
      // No more unarchived threads. Clear selection.
      navigate('/leasing/inbox').catch(handleError)
    }
  }

  // Used for keyboard j/k navigation and after archiving a thread
  // Either go down to the next thread (1) or go up (-1).
  private onNav(offset: -1 | 1) {
    const { selectedThread, threads } = this.state

    if (threads == null) {
      return
    }

    const threadIx = threads.findIndex(t => t.id === selectedThread?.id)
    if (threadIx < 0) {
      return
    }

    const newThreadIx = threadIx + offset
    if (newThreadIx < 0 || newThreadIx >= threads.length) {
      return
    }
    const newThread = threads[newThreadIx]
    console.log(NAV_THREAD_FMT, newThread.id)
    navigate(getThreadPath(newThread.id)).catch(handleError)
  }

  private navTothreadWithLeastTimeLeft() {
    const { threads } = this.state
    if (threads == null) {
      return
    }
    const threadsWithTimer = threads.filter(t => t.botThread != null && t.botThread.showCountdown)
    if (threadsWithTimer.length === 0) {
      // no threads with timer at the moment
      return
    }
    const threadWithLeastTimeLeft = threadsWithTimer.sort((a, b) => {
      // sort by sendTime
      const sendTimeA = a.botThread?.sendTime
      const sendTimeB = b.botThread?.sendTime
      if (sendTimeA != null && sendTimeB != null) {
        if (sendTimeA < sendTimeB) {
          return -1
        }
        if (sendTimeA > sendTimeB) {
          return 1
        }
      }
      // when sendTimes are the same, resort to sortTime
      return b.sortTime.localeCompare(a.sortTime)
    })[0]
    // navigate to the thread with the least amount of time left in timer
    console.log(NAV_THREAD_FMT, threadWithLeastTimeLeft.id)
    navigate(getThreadPath(threadWithLeastTimeLeft.id)).catch(handleError)
  }

  private onKeyboardBound = (action: KeyAction) => {
    let offset: 1 | -1
    if (action === KeyAction.down) {
      offset = 1
    } else if (action === KeyAction.up) {
      offset = -1
    } else if (action === KeyAction.bottom) {
      const { threads } = this.state
      if (threads == null) {
        return
      }
      const lastThread = threads[threads.length - 1]
      console.log(NAV_THREAD_FMT, lastThread.id)
      navigate(getThreadPath(lastThread.id)).catch(handleError)
      return
    } else if (action === KeyAction.goToLeastTimer) {
      this.navTothreadWithLeastTimeLeft()
      return
    } else {
      return
    }
    this.onNav(offset)
  }
}

const InboxWrap = styled.div`
  min-width: 1280px;
  height: 100%;
  display: flex;
`

const InboxContent = styled.div`
  display: flex;
  flex: 1;
`

type TaskTabType = 'NEW_TASK'

interface TasksPageProps extends RouteComponentProps, AppRouteParams {
  user: FetchUserContextQuery['self']
  /**
   * Set if we are viewing a tab of the tasks page (e.g. new task).
   */
  tab?: TaskTabType
}

export class TasksPage extends React.PureComponent<TasksPageProps> {
  render() {
    const { threadId, taskId, contactId } = this.props

    const variables: FetchTasksPageQueryVariables = {
      ...routeParams(this.props),
      hasThreadId: Boolean(threadId),
      hasTaskId: Boolean(taskId),
      hasTaskIdAndContactId: Boolean(taskId) && Boolean(contactId),
    }

    return (
      <PollingQuery query={fetchTasksPageQuery} variables={variables}>
        {(res: QueryResult<FetchTasksPageQuery, FetchTasksPageQueryVariables>) => (
          <TasksPageImpl {...this.props} {...res} />
        )}
      </PollingQuery>
    )
  }
}

type TasksPageImplProps = TasksPageProps & QueryResult<FetchTasksPageQuery, FetchTasksPageQueryVariables>

interface TasksPageImplState {
  data?: FetchTasksPageQuery
  selectedTask?: FetchTasksPageQuery['task'] | null
  selectedThread?: FetchTasksPageQuery['thread'] | null
}

class TasksPageImpl extends React.PureComponent<TasksPageImplProps, TasksPageImplState> {
  constructor(props: TasksPageImplProps) {
    super(props)
    this.state = {
      selectedTask: null,
      selectedThread: null,
    }
  }

  static getDerivedStateFromProps(props: TasksPageImplProps, state: TasksPageImplState): TasksPageImplState {
    const { threadId, taskId, contactId } = props
    const data = props.data || state.data

    if (data == null) {
      return { data }
    }

    return TasksPageImpl.computeDerivedState(data, threadId, taskId, contactId)
  }

  private static computeDerivedState = (
    data: FetchTasksPageQuery,
    threadId?: string,
    taskId?: string,
    contactId?: string
  ) => {
    const { allFirmTasks } = data

    const selectedThread = data.thread

    if (selectedThread && !selectedThread.isArchived) {
      maybeNotifyUpdate(selectedThread)
    }

    // If we know exactly what to show, wait for it to load
    if (taskId != null && threadId == null && contactId == null) {
      // We are on a page for a specific task
      if (data.task) {
        return { data, selectedTask: data.task, selectedThread: null }
      }
    } else if (threadId != null) {
      // We are on a page for a specific thread
      if (data.thread) {
        return { data, selectedTask: null, selectedThread: data.thread }
      }
    } else if (taskId != null && contactId != null) {
      // We're redirecting to a thread
      if (data.contactThread) {
        // Redirect from /inbox/tasks/threads/<task-id>/<contact-id> to /inbox/tasks/threads/<thread-id>
        navigate(getThreadPath(data.contactThread.id), { replace: true }).catch(handleError)
      }
    } else if (allFirmTasks.length > 0) {
      // We are on the tasks page. Show the first task
      // Redirect from /tasks to eg /tasks/task/123
      navigate(getTaskPath(allFirmTasks[0].id), { replace: true }).catch(handleError)
    }

    return { data }
  }

  render() {
    const { threadId, user } = this.props
    const { data } = this.state

    if (data == null) {
      return null
    }

    console.log('TasksPage render: taskId %s task %s', data.task?.id, data.task?.displayName)

    const { allFirmTasks } = data
    const { selectedTask, selectedThread } = this.state

    const tasks = [] as FetchTasksPageQuery['allFirmTasks']

    // add leasing firm or leasing property
    if (selectedTask != null) {
      tasks.push(selectedTask)
    } else if (selectedThread != null) {
      tasks.push(selectedThread.leasingTask)
    }

    // add leasing firms
    const firstId = tasks.length === 0 ? 0 : tasks[0].id
    tasks.push(...allFirmTasks.filter(ft => ft.id !== firstId))

    let content = null
    let contentComponentName = '' // for the error boundary

    if (selectedThread) {
      content = <ThreadDetail user={user} thread={selectedThread} onArchiveThread={this.onArchiveBound} />
      contentComponentName = 'ThreadDetail'
    } else if (selectedTask) {
      content = <TaskDetail user={user} task={selectedTask} />
      contentComponentName = 'TaskDetail'
    }

    return (
      <InboxWrap>
        <InlineErrorBoundary componentName="TasksSidebar">
          <TasksSidebar
            tasks={tasks}
            selectedTaskId={selectedTask?.id}
            selectedThreadId={threadId ? Number(threadId) : undefined}
            user={user}
          />
        </InlineErrorBoundary>
        <InboxContent>
          <InlineErrorBoundary componentName={contentComponentName}>{content}</InlineErrorBoundary>
        </InboxContent>
      </InboxWrap>
    )
  }

  private onArchiveBound = () => {
    const { selectedTask } = this.state
    if (selectedTask != null) {
      navigate(getTaskPath(selectedTask.id)).catch(handleError)
    }

    // TODO: stop double fetching every time the thread id changes
  }
}
